import { useAuthStore } from '../store/useAuthStore';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { OfflineFirstStorage } from '../repositories/OfflineFirstStorage';

export class TaskCompletionService {
    /**
     * Notify the sync-server that a task has been completed by the active student.
     * Retries transient network glitches and queues to OfflineFirstStorage if offline
     * or unreachable so completions are flushed upon reconnection.
     */
    static async markTaskComplete(
        studentId: string,
        assignmentId: string,
        serverUrl: string,
        score?: number
    ): Promise<void> {
        if (!studentId || !assignmentId || !serverUrl) return;

        // If explicitly offline, queue immediately without waiting for timeouts
        if (typeof (globalThis as any).navigator !== 'undefined' && (globalThis as any).navigator.onLine === false) {
            console.log(`[TaskCompletionService] Device offline, queuing task completion: ${assignmentId}`);
            await OfflineFirstStorage.savePendingTaskCompletion({
                studentId,
                assignmentId,
                serverUrl,
                score,
                timestamp: Date.now(),
            });
            return;
        }

        try {
            const apiBase = String(serverUrl).replace(/\/+$/, '');
            const response = await fetchWithRetry(`${apiBase}/api/students/${studentId}/tasks/${assignmentId}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${useAuthStore.getState().deviceToken}`
                },
                body: JSON.stringify({ score }),
                maxRetries: 2,
                timeoutMs: 6000,
            });

            if (!response.ok) {
                console.warn(`[TaskCompletionService] Task completion server returned ${response.status} ${response.statusText}`, response);
                await OfflineFirstStorage.savePendingTaskCompletion({
                    studentId,
                    assignmentId,
                    serverUrl,
                    score,
                    timestamp: Date.now(),
                });
            }
        } catch (e) {
            console.warn('[TaskCompletionService] Failed to mark task complete on server:', e);
            await OfflineFirstStorage.savePendingTaskCompletion({
                studentId,
                assignmentId,
                serverUrl,
                score,
                timestamp: Date.now(),
            });
        }
    }
}
